import * as turf from "@turf/turf"
import type { LineString, Point } from "geojson"
import { memo, useCallback, useMemo } from "react"
import { v4 as uuid } from "uuid"

import { useList } from "../../hooks/utils"
import {
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler, UUIDFeature, UUIDFeatureCollection } from "../typings"
import { wrapGeometryInFeature } from "../utils"
import { Layer, Source } from "./memo"

const LINE_STRING_DRAWER_SOURCE_ID = "line-string-drawer-source"

enum LineStringDrawerLayedId {
	CIRCLE = "line-string-drawer-circle-layer",
	LINE = "line-string-drawer-line-layer",
}

interface LineStringDrawerProps {
	onComplete?: (lineString: LineString) => void
}

export const LineStringDrawer = memo<LineStringDrawerProps>((props) => {
	const { onComplete } = props

	const { items: points, add: addPoint, removeBy: removePointBy } = useList<UUIDFeature<Point>>([])

	const handleLineStringMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { point, target, lngLat } = e

			const features = target.queryRenderedFeatures(point, {
				layers: [LineStringDrawerLayedId.CIRCLE],
			})

			const feature = features.length > 0 ? features[0] : undefined

			if (points.length > 0 && feature && feature.id === points[points.length - 1].properties.uuid) {
				const lineString = turf.lineString(points.map(({ geometry }) => geometry.coordinates))

				onComplete?.(lineString.geometry)
			} else if (feature) {
				removePointBy(({ properties }) => properties?.uuid !== feature.id)
			} else {
				addPoint(wrapGeometryInFeature(turf.point(lngLat.toArray()).geometry, { uuid: uuid() }))
			}
		},
		[addPoint, onComplete, points, removePointBy],
	)

	useCurrentMapEvent("click", handleLineStringMapClick)

	const geojson = useMemo<UUIDFeatureCollection>(() => {
		const features: UUIDFeature[] = []

		if (points.length > 0) {
			features.push(...points)
		}

		if (points.length >= 2) {
			features.push(
				wrapGeometryInFeature(turf.lineString(points.map(({ geometry }) => geometry.coordinates)).geometry, {
					uuid: uuid(),
				}),
			)
		}

		return turf.featureCollection(features)
	}, [points])

	return (
		<Source id={LINE_STRING_DRAWER_SOURCE_ID} type="geojson" data={geojson} promoteId="uuid">
			<Layer
				id={LineStringDrawerLayedId.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={LineStringDrawerLayedId.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={LineStringDrawerLayedId.CIRCLE}
			/>
		</Source>
	)
})
LineStringDrawer.displayName = "LineStringDrawer"
